/**
 * Plan-approved → build-running handoff policy for the new UI.
 *
 * See `docs/plan-build-handoff-implementation-spec.md` for the user-facing
 * contract and regression map.
 */

import type { BuildGameInterviewStatus } from '@volley/crucible-ipc-types/build-game-types';
import {
  isPreviewPhaseGated,
  previewPhaseGateTooltip,
  type PreviewPhaseGate,
} from '../components/PreviewLaunchButton';
import type { WorkspaceRunKind } from './run-state';
import type { PreviewDevLoopKind, PreviewDevLoopState } from './preview-state';

export type WorkspaceHandoffMode = 'chat' | 'plan' | 'preview' | 'design';

/** Three-zone build layout: preview only while Phase B is actively running. */
export function showNewUiBuildViewFor(runStateKind: WorkspaceRunKind): boolean {
  return runStateKind === 'building';
}

export function initialWorkspaceModeForHandoff(args: {
  buildView: boolean;
  previewReady: boolean;
  showPlanModeView: boolean;
  planReadyMode?: Extract<WorkspaceHandoffMode, 'plan' | 'design'>;
  createHandoffMode?: Extract<WorkspaceHandoffMode, 'design'> | null;
}): WorkspaceHandoffMode {
  if (args.createHandoffMode === 'design') return 'design';
  if (args.previewReady) return 'preview';
  if (args.showPlanModeView) return args.planReadyMode ?? 'plan';
  return 'chat';
}

/**
 * Locks plan markdown edits after approval / during build.
 *
 * `showBackToPlan` covers quick committed/build when run-state lags.
 * `interviewStatus` covers stale `building` while the rail shows waiting.
 */
export function planRevisionLockedForHandoff(args: {
  runStateKind: WorkspaceRunKind;
  showBackToPlan: boolean;
  interviewStatus: BuildGameInterviewStatus | null | undefined;
}): boolean {
  if (args.runStateKind === 'build-kickoff-pending' || args.runStateKind === 'building') {
    return true;
  }
  if (args.showBackToPlan) return true;
  return args.interviewStatus === 'dispatched' || args.interviewStatus === 'building';
}

export function previewHandoffDisabledReasonForHandoff(args: {
  runStateKind: WorkspaceRunKind;
  previewDevLoopKind: PreviewDevLoopKind;
  previewPhaseGate: PreviewPhaseGate | undefined;
}): string | undefined {
  if (args.previewDevLoopKind !== 'idle') return undefined;
  if (args.previewPhaseGate !== undefined) return undefined;
  if (args.runStateKind === 'build-kickoff-pending' || args.runStateKind === 'building') {
    return 'Preview will unlock once the build reaches runnable code.';
  }
  return undefined;
}

export function mergePreviewDisabledReason(
  handoffReason: string | undefined,
  devLoopDisabledReason: string | undefined,
): string | undefined {
  return handoffReason ?? devLoopDisabledReason;
}

export function previewAvailableTitleForDevLoop(devLoop: PreviewDevLoopState): string {
  if (devLoop.kind === 'ready') {
    return 'Preview running — open display and controllers.';
  }
  if (devLoop.kind === 'idle') {
    return 'Start preview to launch display and controllers.';
  }
  return devLoop.detail ?? devLoop.label;
}

export type PreviewLaunchPillIcon = 'spinner' | 'lock' | 'play';

export interface PreviewLaunchPillState {
  disabled: boolean;
  phaseGated: boolean;
  title: string;
  icon: PreviewLaunchPillIcon;
}

/** Header Preview pill disabled state, title, and icon variant. */
export function derivePreviewLaunchPillState(args: {
  gameId: string | undefined;
  previewLoading: boolean;
  previewForceDisabled: boolean;
  previewDisabledReason: string | undefined;
  previewAvailableTitle: string | undefined;
  previewPhaseGate: PreviewPhaseGate | undefined;
}): PreviewLaunchPillState {
  const phaseGated = isPreviewPhaseGated(args.previewPhaseGate);
  const disabled = !args.gameId || args.previewForceDisabled || args.previewLoading || phaseGated;
  const title = !args.gameId
    ? 'Preview is unavailable until a game is loaded'
    : args.previewDisabledReason
      ? args.previewDisabledReason
      : args.previewLoading
        ? 'Starting the dev server…'
        : phaseGated && args.previewPhaseGate !== undefined
          ? previewPhaseGateTooltip(args.previewPhaseGate)
          : (args.previewAvailableTitle ?? 'Open the live preview');
  const icon: PreviewLaunchPillIcon = args.previewLoading
    ? 'spinner'
    : phaseGated || args.previewForceDisabled
      ? 'lock'
      : 'play';
  return { disabled, phaseGated, title, icon };
}
